
import React, { useState } from 'react';
import { format, isToday } from 'date-fns';
import { Calendar } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import ViewAllMealsDialog from './view-all-meals-dialog';
import MealTypeSections from './daily-tile/MealTypeSections';
import StatusDot from './daily-tile/StatusDot';
import MealTypeTabs from './daily-tile/MealTypeTabs';
import AllMealTypesDisplay from './daily-tile/AllMealTypesDisplay';

export interface MealInfo {
  type: 'recipe' | 'composition' | 'note';
  id: string;
  name: string;
  isQuickRecipe?: boolean;
  prepTime?: string;
  cookTime?: string;
  recipeCount?: number;
  mealPlanId?: string;
  mealNoteId?: string;
  mealType: 'breakfast' | 'lunch' | 'dinner';
  onRemove?: (mealPlanId: string) => void;
  onRemoveNote?: (mealNoteId: string) => void;
  authorFirstName?: string;
  imageUrl?: string;
  storageImageUrl?: string;
  userImageUrl?: string;
  storageImagePath?: string;
  userImagePath?: string;
}

export interface DailyTileProps {
  date: Date;
  meals?: MealInfo[];
  isLoading?: boolean;
  showTodayBadge?: boolean;
  showStatusDot?: boolean;
  footer?: React.ReactNode;
  className?: string;
  variant?: 'default' | 'today' | 'past';
  customMealContent?: React.ReactNode;
  mealMode?: 'breakfast' | 'lunch' | 'dinner';
  onMealTypeChange?: (mealType: 'breakfast' | 'lunch' | 'dinner') => void;
  hasBreakfast?: boolean;
  hasLunch?: boolean;
  hasDinner?: boolean;
  isEditable?: boolean;
  showAllMealTypes?: boolean;
  heightMode?: 'fixed' | 'flexible';
  primaryMealType?: string; // For onboarding context
  showReviewButton?: boolean;
  isYesterday?: boolean;
  hideBadge?: boolean;
}

const DailyTile: React.FC<DailyTileProps> = ({
  date,
  meals = [],
  isLoading = false,
  showTodayBadge = true,
  showStatusDot = false,
  footer,
  className = '',
  variant = 'default',
  customMealContent,
  mealMode = 'breakfast',
  onMealTypeChange,
  hasBreakfast = false,
  hasLunch = false,
  hasDinner = false,
  isEditable = true,
  showAllMealTypes = false,
  heightMode = 'flexible',
  primaryMealType,
  showReviewButton = false,
  isYesterday = false,
  hideBadge = false
}) => {
  const [showAllMealsDialog, setShowAllMealsDialog] = useState(false);
  const today = isToday(date);
  const dayName = format(date, 'EEEE');
  const dayDate = format(date, 'MMM d');
  const isWeekend = date.getDay() === 0 || date.getDay() === 6; // Sunday or Saturday

  const getCardClassName = () => {
    const baseClasses = 'hover:shadow-md transition-shadow';
    if (today) return `${baseClasses} border-primary bg-primary/5`;
    if (variant === 'past') return `${baseClasses} bg-gray-50`;
    if (isWeekend) return `${baseClasses} bg-muted/30`;
    return baseClasses;
  };

  return (
    <>
      <Card className={`${getCardClassName()} ${className} ${footer ? 'flex flex-col min-h-[200px]' : ''}`}>
        <CardHeader className="pb-2">
           <CardTitle className="text-sm flex items-center justify-between">
             <div className="flex items-center gap-2">
                <p className="font-semibold text-foreground">
                  {dayName} {dayDate}
                  {primaryMealType && (
                    <span className="ml-2 text-sage font-medium capitalize">
                      {primaryMealType}
                    </span>
                  )}
                </p>
               {isWeekend && (
                 <Calendar className="h-3 w-3 text-muted-foreground" />
               )}
             </div>
            <div className="flex items-center gap-2">
              {isEditable && onMealTypeChange && (
                <MealTypeTabs
                  activeMealType={mealMode}
                  onMealTypeChange={onMealTypeChange}
                  hasBreakfast={hasBreakfast}
                  hasLunch={hasLunch}
                  hasDinner={hasDinner}
                  isEditable={isEditable}
                />
              )}
            </div>
          </CardTitle>
        </CardHeader>
        
        <CardContent className={`flex flex-col ${footer ? 'flex-1' : ''}`}>
          <div className={`${heightMode === 'fixed' ? (footer ? 'min-h-[4rem] max-h-[5.5rem]' : 'min-h-[5rem] max-h-[8rem]') : 'min-h-[6rem]'} flex items-start justify-start w-full overflow-hidden`}>
            {customMealContent ? (
              customMealContent
            ) : showAllMealTypes ? (
              <AllMealTypesDisplay
                meals={meals}
                isLoading={isLoading}
                onShowAllMeals={() => setShowAllMealsDialog(true)}
                heightMode={heightMode}
                showReviewButton={showReviewButton}
                isYesterday={isYesterday}
              />
            ) : (
              <MealTypeSections
                meals={meals}
                isLoading={isLoading}
                onShowAllMeals={() => setShowAllMealsDialog(true)}
                mealMode={mealMode}
                hideBadge={hideBadge}
              />
            )}
          </div>
          
          {showStatusDot && <StatusDot meals={meals} />}
        </CardContent>
        
        {footer && (
          <CardFooter className="pt-2 mt-auto">
            <div className="w-full flex flex-wrap gap-1.5 justify-center overflow-hidden">
              <div className="w-full max-w-full min-w-0 text-xs">
                {footer}
              </div>
            </div>
          </CardFooter>
        )}
      </Card>

      <ViewAllMealsDialog
        open={showAllMealsDialog}
        onOpenChange={setShowAllMealsDialog}
        date={date}
        meals={meals}
      />
    </>
  );
};

export default DailyTile;
